import styled from 'styled-components';
import { STATUS_COLORS, ACTION_COLORS } from './constants';

// ─── Page (index.jsx) ────────────────────────────────────────────────────────

export const Page = styled.div`
  padding: 24px 28px;
  min-height: 100vh;
  background: #f8f9fa;
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`;

export const TitleBlock = styled.div``;

export const Title = styled.h1`
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
  color: #228be6;
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const Btn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid #d0ebff;
  background: white;
  color: #228be6;

  &:hover {
    background: #e7f5ff;
    border-color: #228be6;
  }
`;

export const Body = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
`;

export const MainColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// ─── Table (ActivityLogTable.jsx) ────────────────────────────────────────────

export const TableWrapper = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 220px);
`;

export const TableScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

export const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

export const Th = styled.th`
  padding: 10px 14px;
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  white-space: nowrap;
`;

export const Tbody = styled.tbody``;

export const Tr = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background 0.1s;
  background: ${props => (props.$highlighted ? '#e7f5ff' : 'transparent')};

  &:hover {
    background: ${props => (props.$highlighted ? '#d0ebff' : '#f8f9fa')};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const Td = styled.td`
  padding: 10px 14px;
  color: #374151;
  vertical-align: middle;
`;

export const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 12px;
  background: ${props => STATUS_COLORS[props.$status]?.bg ?? '#f3f4f6'};
  color: ${props => STATUS_COLORS[props.$status]?.text ?? '#374151'};
`;

export const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => STATUS_COLORS[props.$status]?.dot ?? '#9ca3af'};
`;

export const ActionBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${props => ACTION_COLORS[props.$action] ?? ACTION_COLORS.default};
`;

export const ResourceType = styled.span`
  font-size: 11px;
  color: #9ca3af;
  text-transform: capitalize;
`;

export const UserCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const UserName = styled.span`
  font-weight: 500;
  color: #111827;
`;

export const UserEmail = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
`;

export const PageButtons = styled.div`
  display: flex;
  gap: 4px;
`;

export const PageBtn = styled.button`
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: ${props => (props.$active ? '#228BE6' : 'white')};
  color: ${props => (props.$active ? 'white' : '#374151')};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.1s;

  &:hover:not(:disabled) {
    background: ${props => (props.$active ? '#1c7ed6' : '#f8f9fa')};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const EmptyRow = styled.tr`
  td {
    padding: 48px 16px;
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
  }
`;

export const TableSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #228be6;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ─── Detail panel (ActivityLogDetailPanel.jsx) ───────────────────────────────

export const Drawer = styled.div`
  width: ${({ $width }) => $width ?? 400}px;
  min-width: 320px;
  max-width: 800px;
  flex-shrink: 0;
  align-self: flex-start;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  height: fit-content;
  max-height: calc(100vh - 160px);
  position: sticky;
  top: 80px;
`;

export const DrawerResizeHandle = styled.div`
  width: 5px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
  transition: background 0.15s;
  &:hover,
  &[data-dragging='true'] {
    background: #bfdbfe;
  }
`;

export const DrawerContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const DrawerHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

export const DrawerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

export const DrawerSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

export const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 18px;
  line-height: 1;
  padding: 0 4px;
  &:hover {
    color: #374151;
  }
`;

export const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

export const Field = styled.div`
  margin-bottom: 14px;
`;

export const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin-bottom: 3px;
`;

export const FieldValue = styled.div`
  font-size: 13px;
  color: #111827;
  word-break: break-all;
`;

export const ErrorBox = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 10px 12px;
  color: #dc2626;
  font-size: 12px;
  word-break: break-word;
`;

export const DrawerSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top-color: #228be6;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin: 24px auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ─── Metadata: context link (project deep-link) ─────────────────────────────

export const ContextLink = styled.a`
  color: #228be6;
  text-decoration: none;
  font-size: 13px;
  &:hover {
    text-decoration: underline;
  }
`;

export const ReferenceLink = styled.a`
  color: #228be6;
  text-decoration: none;
  font-size: 12px;
  font-family: monospace;
  word-break: break-all;
  display: block;
  &:hover {
    text-decoration: underline;
  }
`;

// ─── AI Analytics Chat Panel ─────────────────────────────────────────────────

export const AiBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid #d0ebff;
  background: ${props => (props.$active ? '#228be6' : 'white')};
  color: ${props => (props.$active ? 'white' : '#228be6')};

  &:hover {
    background: ${props => (props.$active ? '#1c7ed6' : '#e7f5ff')};
    border-color: #228be6;
  }
`;

export const ChatPanel = styled.div`
  width: ${({ $width }) => $width ?? 380}px;
  min-width: 320px;
  max-width: 800px;
  flex-shrink: 0;
  align-self: flex-start;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  max-height: calc(100vh - 180px);
  position: sticky;
  top: 80px;
`;

export const ChatContent = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ChatHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f9fafb;
  flex-shrink: 0;
`;

export const ChatHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #228be6;
`;

export const ChatMessages = styled.div`
  overflow-y: auto;
  max-height: calc(100vh - 350px);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scroll-behavior: smooth;
`;

export const ChatBubble = styled.div`
  max-width: 88%;
  padding: 10px 13px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.55;
  word-break: break-word;
  align-self: ${props => (props.$role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${props => (props.$role === 'user' ? '#228be6' : '#f9fafb')};
  color: ${props => (props.$role === 'user' ? 'white' : '#374151')};
  border-bottom-right-radius: ${props => (props.$role === 'user' ? '4px' : '12px')};
  border-bottom-left-radius: ${props => (props.$role === 'user' ? '12px' : '4px')};

  p:last-child {
    margin-bottom: 0;
  }

  /* Keep user-bubble inline code readable */
  code {
    background: ${props => (props.$role === 'user' ? 'rgba(255,255,255,0.2)' : 'transparent')};
  }
`;

export const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 2px 4px;
  align-self: flex-start;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #9ca3af;
    animation: bounce 1.2s infinite;
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  @keyframes bounce {
    0%,
    60%,
    100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-6px);
    }
  }
`;

export const ChatInputArea = styled.div`
  padding: 12px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 8px;
  align-items: flex-end;
  flex-shrink: 0;
  background: #f9fafb;
`;

export const ChatTextarea = styled.textarea`
  flex: 1;
  resize: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.5;
  color: #374151;
  outline: none;
  max-height: 120px;
  overflow-y: auto;
  transition: border-color 0.15s;

  &:focus {
    border-color: #228be6;
    box-shadow: 0 0 0 3px rgba(34, 139, 230, 0.12);
  }

  &::placeholder {
    color: #9ca3af;
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
  }
`;

export const SendBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  background: #228be6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #1c7ed6;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const ChatEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #6b7280;
  padding: 24px;
  text-align: center;

  svg {
    opacity: 0.5;
  }

  p {
    font-size: 13px;
    margin: 0;
    line-height: 1.5;
  }
`;

export const SuggestedQuestions = styled.div`
  padding: 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  overflow-y: auto;
`;

export const SuggestBtn = styled.button`
  text-align: left;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #d0ebff;
  background: white;
  color: #228be6;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #e7f5ff;
    border-color: #228be6;
  }
`;

// ─── Metadata: diff / changes table ─────────────────────────────────────────

export const DiffTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;
`;

export const DiffRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  &:last-child {
    border-bottom: none;
  }
`;

export const DiffCell = styled.td`
  padding: 4px 6px;
  vertical-align: top;
  color: #6b7280;
  width: 30%;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
`;

export const DiffBefore = styled.span`
  background: #fef2f2;
  color: #dc2626;
  border-radius: 3px;
  padding: 1px 5px;
  font-family: monospace;
  font-size: 11px;
  word-break: break-all;
`;

export const DiffAfter = styled.span`
  background: #f0fdf4;
  color: #16a34a;
  border-radius: 3px;
  padding: 1px 5px;
  font-family: monospace;
  font-size: 11px;
  word-break: break-all;
`;
